import React from 'react';
import styles from './RawMaterialMapColumnCard.module.scss';
import { ReactComponent as RawMaterialIcon } from 'assets/icons/raw_material.svg';
import clsx from 'clsx';
import { Tooltip, Typography } from 'antd';
import EmissionCalculationError from '../EmissionCalculationError';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import formatNumberWithCommas from 'shared/utils/formatNumberWithComma';

interface componentPropsInterface {
  style?: React.CSSProperties;
}

export enum ExpandIconState {
  OPEN = 'open',
  CLOSED = 'closed',
}
export interface DataColumnCardProps {
  name?: string;
  weight?: string;
  carbon?: string;
  emission: number;
  unit?: string;
  id?: string;
  isOrigin?: boolean;
  isTop?: boolean;
  isBottom?: boolean;
  expandIcon?: ExpandIconState;
  isDownloadPage?: boolean;
}

const DataColumnCard: React.FC<
  DataColumnCardProps & componentPropsInterface
> = ({
  name,
  weight,
  carbon,
  emission,
  unit,
  id,
  isOrigin,
  expandIcon,
  isTop,
  isBottom,
  isDownloadPage,
  ...componentProps
}: DataColumnCardProps & componentPropsInterface) => {
  const handleColorByEmission = (value: number) => {
    let category = '';
    if (value >= 0 && value <= 20) {
      category = 'blue';
    } else if (value > 20 && value <= 40) {
      category = 'green';
    } else if (value > 40 && value <= 60) {
      category = 'yellow';
    } else if (value > 60 && value <= 80) {
      category = 'orange';
    } else if (value > 80) {
      category = 'red';
    }

    return category;
  };

  const isError = parseFloat(carbon ?? '') < 0;

  const carbonEmissionValue = parseFloat(carbon ?? '').toFixed(4);

  const { style } = componentProps;

  const colorCodingClass = {
    [styles.green]: handleColorByEmission(emission) === 'green',
    [styles.blue]: handleColorByEmission(emission) === 'blue',
    [styles.yellow]: handleColorByEmission(emission) === 'yellow',
    [styles.orange]: handleColorByEmission(emission) === 'orange',
    [styles.red]: handleColorByEmission(emission) === 'red',
  };
  return (
    <div style={style} className="canvas-card" id={id}>
      <EmissionCalculationError showError={isError}>
        <div
          className={clsx(
            {
              [styles.cardContainer]: true,
              [styles.originCard]: isOrigin,
              [styles.hasTooltip]: !isDownloadPage,
              [styles.top]: isTop,
              [styles.bottom]: isBottom,
              [styles.error]: isError,
              ['no-drag']: true, //Classname for Reactflow
            },
            !isOrigin && colorCodingClass
          )}>
          <div className={styles.contentContainer}>
            {!isOrigin && (
              <div className={styles.iconContainer}>
                <RawMaterialIcon />
              </div>
            )}
            {isOrigin && (
              <div className={clsx(styles.originColor, colorCodingClass)}></div>
            )}
            <div className={styles.dataContainer}>
              <div className={styles.label}>
                <Tooltip title={name}>
                  <Typography.Paragraph
                    ellipsis={!isDownloadPage && { rows: 1 }}>
                    {name}
                  </Typography.Paragraph>
                </Tooltip>
              </div>
              <div className={styles.data}>
                <div className={styles.weight}>
                  <h3>
                    {parseFloat(weight ?? '').toFixed(2)} {unit}
                  </h3>
                </div>
                <div className={styles.carbon}>
                  <p>
                    {formatNumberWithCommas(carbonEmissionValue)} kgCO
                    <sub>2</sub>e
                  </p>
                </div>
              </div>
            </div>
            {expandIcon && (
              <div
                className={clsx({
                  [styles.expandIcon]: true,
                  [styles.expanded]: expandIcon === ExpandIconState.OPEN,
                })}>
                <Arrow fontSize={44} />
              </div>
            )}
          </div>
        </div>
      </EmissionCalculationError>
    </div>
  );
};

export default DataColumnCard;
